import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Pagination, Button } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DownloadOutlined } from '@ant-design/icons';
import { isEmpty, lowerCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useUI } from 'store/UI/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import styled from 'styled-components';
import { 
  handleTypeText, 
  allCategories, 
  allTenseData, 
  getMainForQuestionType,
  moodsOption, 
  pronounOptions, 
  question_type_generate_more_worksheet 
} from '../helper';
import FilterCard from './FilterCard';

const CountPreview = ({ countPreview, setCountPreview }) => {
  const { countConfigWorksheet } = useUI();
  const showAlert = useAlert();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: 'asc',
  });
  const [pagination, setPagination] = useState({
    totalLength: 0,
    pageSize: 100,
    current: 1,
    pageSizeOptions: [],
    showSizeChanger: false,
  });
  const [questionType, setQuestionType] = useState([]);
  const [selectedMoodOption, setSelectedMoodOption] = useState([]);
  const [tense, setTense] = useState([]);
  const [category, setCategory] = useState([]);
  const [pronouns, setPronouns] = useState([]);
  const [sortByCount, setSortByCount] = useState(null);
  const [bugFilter, setBugFilter] = useState(false);

  const filteredAndSortedData = useMemo(() => {
    if (isEmpty(countConfigWorksheet)) return [];

    let filtered = [...countConfigWorksheet];

    if (questionType?.length > 0) {
      const questionTypeMains = getMainForQuestionType(
        questionType,
        question_type_generate_more_worksheet
      );
      filtered = filtered.filter((item) => {
        const itemType = item.type || '';
        return questionTypeMains.some((main) => 
          lowerCase(itemType) === lowerCase(main)
        );
      });
    }

    if (selectedMoodOption?.length > 0) {
      const moodLabels = moodsOption
        .filter((opt) => selectedMoodOption.includes(opt.value))
        .map((opt) => opt.label);
      filtered = filtered.filter((item) => {
        return moodLabels.some((label) => 
          lowerCase(item.mood || '') === lowerCase(label)
        );
      });
    }

    if (tense?.length > 0) {
      const alltense = allTenseData();
      const tenseLabels = alltense
        .filter((opt) => tense.includes(opt.value))
        .map((opt) => opt.label);
      filtered = filtered.filter((item) => {
        return tenseLabels.some((label) => 
          lowerCase(item.tense || '') === lowerCase(label)
        );
      });
    }

    if (category?.length > 0) {
      const categoryData = allCategories();
      const categoryLabels = categoryData
        .filter((opt) => category.includes(opt.value))
        .map((opt) => opt.label);
      filtered = filtered.filter((item) => {
        return categoryLabels.some((label) => 
          lowerCase(item.category || '') === lowerCase(label)
        );
      });
    }

    if (pronouns?.length > 0) {
      const pronounLabels = pronounOptions
        .filter((opt) => pronouns.includes(opt.value))
        .map((opt) => opt.label);
      filtered = filtered.filter((item) => {
        const itemPronouns = (item.pronouns || '').split(',').map(p => lowerCase(p.trim()));
        return pronounLabels.some((label) => 
          itemPronouns.includes(lowerCase(label))
        );
      });
    }

    if (searchQuery.trim().length > 0) {
      const searchText = lowerCase(searchQuery);
      filtered = filtered.filter((item) => {
        return (
          lowerCase(item.type || '').includes(searchText) ||
          lowerCase(item.mood || '').includes(searchText) ||
          lowerCase(item.tense || '').includes(searchText) ||
          lowerCase(item.category || '').includes(searchText) ||
          lowerCase(item.pronouns || '').includes(searchText) ||
          String(item.count || '').includes(searchText)
        );
      });
    }

    if (sortByCount) {
      filtered.sort((a, b) => {
        const aCount = Number(a.count) || 0;
        const bCount = Number(b.count) || 0;
        if (sortByCount === 'high') {
          return bCount - aCount;
        } else {
          return aCount - bCount;
        }
      });
    } else if (sortConfig.field) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.field];
        let bValue = b[sortConfig.field];

        if (sortConfig.field === 'count') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }

        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    return filtered;
  }, [countConfigWorksheet, searchQuery, sortConfig, questionType, selectedMoodOption, tense, category, pronouns, sortByCount, bugFilter]);

  const paginatedData = useMemo(() => {
    if (isEmpty(filteredAndSortedData)) return [];
    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

  const totalCountPerPage = useMemo(() => {
    return paginatedData.reduce((acc, item) => acc + Number(item.count || 0), 0);
  }, [paginatedData]);


  useEffect(() => {
    if (filteredAndSortedData) {
      setPagination((prev) => ({
        ...prev,
        totalLength: filteredAndSortedData.length,
        current: 1,
      }));
    }
  }, [filteredAndSortedData]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [questionType, selectedMoodOption, tense, category, pronouns, sortByCount]);

  const handleSort = (field) => {
    if (sortByCount) {
      setSortByCount(null);
    }
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSortByCountChange = (value) => {
    setSortByCount(value);
    if (value) {
      setSortConfig({ field: null, direction: 'asc' });
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    document.body.scrollTop = 0;
  };

  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return '';
  
    let stringValue = String(value).trim();
    const startsWithFormulaChar = /^[=+\-@-]/.test(stringValue);
  
    if (startsWithFormulaChar) {
      // Wrap in ="..." to display as text safely in Excel
      stringValue = `="${stringValue.replace(/"/g, '""')}"`;
      return stringValue;
    }
  
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      stringValue = `"${stringValue.replace(/"/g, '""')}"`;
    }
  
    return stringValue;
  };    

  const handleDownloadCSV = () => {
    if (isEmpty(filteredAndSortedData)) {
      return;
    }

    const headers = [
      'Sequence Number',
      'Question Type',
      'Mood',
      'Tense',
      'Category',
      'Pronouns',
      'Count'
    ];

    const csvRows = [headers.map(escapeCSVValue).join(',')];

    filteredAndSortedData.forEach((item, index) => {
      const row = [
        index + 1,
        handleTypeText(item?.type || ''),
        item.mood || '',
        item.tense || '',
        item.category || '',
        item.pronouns || '',
        item.count || 0
      ];
      csvRows.push(row.map(escapeCSVValue).join(','));
    });

    const csvContent = csvRows.join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `count_preview_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showAlert(`CSV file downloaded successfully! (${filteredAndSortedData.length} records)`, 'success');
  };

  const handleBugFilter = (value) => {
    setBugFilter(value);
  };

  return (
    <StableWrapper style={{ position: 'relative' }}>
      <FilterCard
        search_by_name={searchQuery}
        setSearchByName={setSearchQuery}
        search_by_questionType={questionType}
        setSearchByQuestionType={setQuestionType}
        search_by_category={category}
        setSearchByCategory={setCategory}
        searchByPronouns={pronouns}
        setSearchByPronouns={setPronouns}
        search_by_tense={tense}
        setSearchByTense={setTense}
        selectedmoodOption={selectedMoodOption}
        setMoodOption={setSelectedMoodOption}
        isCountPreview={true}
        sortByCount={sortByCount}
        setSortByCount={handleSortByCountChange}
        bugFilter={bugFilter}
        setBugFilter={handleBugFilter}
      />
      <DownloadButtonContainer>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCSV}
          disabled={isEmpty(filteredAndSortedData)}
          style={{ minHeight: 38 }}
        >
          Download CSV
        </Button>
      </DownloadButtonContainer>
      <Table
        stickyHeader
        aria-label="sticky table"
        style={{
          width: '100%',
          textAlign: 'center',
          backgroundColor: '#fff',
          marginTop: 20,
        }}
      >
        <TableHeader sortConfig={sortConfig} handleSort={handleSort} paginatedData={filteredAndSortedData} totalCountPerPage={totalCountPerPage} />
        <TableBody>
          {isEmpty(countConfigWorksheet) && (
            <TableRow key={'0'} style={{ border: 0 }}>
              <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                No results
              </TableCell>
            </TableRow>
          )}
          {!isEmpty(countConfigWorksheet) && filteredAndSortedData.length === 0 && (
            <TableRow key={'0'} style={{ border: 0 }}>
              <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                No results found
              </TableCell>
            </TableRow>
          )}
          {!isEmpty(countConfigWorksheet) &&
            filteredAndSortedData.length > 0 &&
            paginatedData.map((item, idx) => {
              const globalIndex = (pagination.current - 1) * pagination.pageSize + idx;
              return (
                <TableRow
                  key={`${item.type}-${item.mood}-${item.tense}-${item.category}-${item.pronouns}-${idx}`}
                >
                  <TableCell>{globalIndex + 1}</TableCell>
                  <TableCell> {handleTypeText(item?.type)}</TableCell>
                  <TableCell>{item.mood}</TableCell>
                  <TableCell>{item.tense}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.pronouns}</TableCell>
                  <TableCell>{item.count}</TableCell>
                </TableRow>
              );
            })}

          {!isEmpty(countConfigWorksheet) && filteredAndSortedData.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.totalLength || 0}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  pageSizeOptions={pagination.pageSizeOptions}
                  onShowSizeChange={(current, size) => {
                    setPagination((prev) => ({
                      ...prev,
                      current: 1,
                      pageSize: size,
                    }));
                    document.body.scrollTop = 0;
                  }}
                  itemRender={(page, type, originalElement) => {
                    if (type === 'page' && page === pagination.current) {
                      return (
                        <div className="ant-pagination-item-active custom-active-page">{page}</div>
                      );
                    }

                    return originalElement;
                  }}
                />
              </TableCell>
              <TableCell colSpan={2} align="right"><p className="totalCountPerPageDisplay">Total count: {totalCountPerPage}</p></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </StableWrapper>
  );
};

const TableHeader = ({ sortConfig, handleSort, paginatedData, totalCountPerPage }) => {
  const SortableHeader = ({ field, label }) => {
    const isActive = sortConfig.field === field;
    return (
      <TableCell
        style={{
          textAlign: 'left',
          fontSize: 16,
          fontWeight: '600',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => handleSort(field)}
      >
        <HeaderContent>
          {label}
          {isActive && (
            <SortIcon>
              {sortConfig.direction === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            </SortIcon>
          )}
        </HeaderContent>
      </TableCell>
    );
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
          Sequence Number. ({paginatedData?.length})
        </TableCell>
        <SortableHeader field="type" label="Question Type" />
        <SortableHeader field="mood" label="Mood" />
        <SortableHeader field="tense" label="Tense" />
        <SortableHeader field="category" label="Category" />
        <SortableHeader field="pronouns" label="Pronouns" />
        <TableCell style={{ textAlign: 'left', fontSize: 16, fontWeight: '600' }}>
          Count ({totalCountPerPage})
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export default CountPreview;

const StableWrapper = styled.div`
  width: 100%;

  overflow-x: auto;
`;

const DownloadButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  margin-bottom: 16px;
`;


const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #1890ff;
  font-size: 12px;
`;
